import { Entity } from '../../models/entity'
import { SandboxClient } from './sandbox-client'
import { SandboxMessage, SandboxMessageType, SandboxPayload } from './protocol'

export interface SandboxApis {
  getStore(): { cacheRecord(record: Entity): void }
}

export interface SandboxError {
  type: string
  message: string
}

export interface SandboxSessionOptions {
  role: 'host' | 'client'
  apis: SandboxApis
  onConnected?: () => void
  onDisconnected?: () => void
  onError?: (error: SandboxError) => void
  onRegisteredHost?: (serverAddress: string) => void
  onEntities?: (schema: Entity, entity: Entity) => void
  onEntityContent?: (entityJson: any) => void
}

export class SandboxSession {
  private client = new SandboxClient()

  constructor(private opts: SandboxSessionOptions) {}

  public connect(serverUrl: string) {
    this.client.start({
      serverUrl,
      onConnected: this.handleConnected,
      onMessageReceived: this.handleMessage,
      onError: (e) => this.opts.onError?.(toSandboxError(e)),
      onDisconnected: this.opts.onDisconnected,
    })
  }

  public close() {
    this.client.close()
  }

  public sendLatestValue(entityJson: any, schemaJson: any) {
    this.client.send({
      type: SandboxMessageType.LatestValue,
      value: { entity: entityJson, schema: schemaJson },
    })
  }

  public sendEntityContent(entityJson: any, schemaJson: any) {
    this.client.send({
      type: SandboxMessageType.UpdateEntityContent,
      value: { entity: entityJson, schema: schemaJson },
    })
  }

  public reconstructEntities(schemaJson: any, entityJson: any): { schema: Entity; entity: Entity } {
    const schema = new Entity(schemaJson, this.opts.apis)
    this.opts.apis.getStore().cacheRecord(schema)
    const entity = new Entity(entityJson, this.opts.apis)
    return { schema, entity }
  }

  private handleConnected = () => {
    if (this.opts.role === 'host') {
      this.client.send({ type: SandboxMessageType.RegisterHost })
    }
    this.opts.onConnected?.()
  }

  private handleMessage = (data: string) => {
    let json: SandboxMessage
    try {
      json = JSON.parse(data)
    } catch (e) {
      this.opts.onError?.({ type: 'message', message: `Received malformed message: ${data}` })
      return
    }

    try {
      switch (json.type) {
        case SandboxMessageType.LatestValue: {
          const { schema, entity } = json.value as SandboxPayload
          const rebuilt = this.reconstructEntities(schema, entity)
          this.opts.onEntities?.(rebuilt.schema, rebuilt.entity)
          break
        }
        case SandboxMessageType.RegisteredHost:
          this.opts.onRegisteredHost?.(json.value.serverAddress)
          break
        case SandboxMessageType.ErrorRegisterHost:
          this.opts.onError?.({ type: 'register-host', message: json.value })
          this.client.close()
          break
        case SandboxMessageType.UpdateEntityContent:
          this.opts.onEntityContent?.(json.value.entity)
          break
      }
    } catch (e: any) {
      this.opts.onError?.({ type: 'message', message: e?.message ?? String(e) })
    }
  }
}

function toSandboxError(e: any): SandboxError {
  return { type: 'connection', message: e?.message ?? '' }
}
